import { useState, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Loader2, Shield, AlertTriangle, Star } from 'lucide-react';
import { useBrand } from '@/config/brands';
import {
  FRAUD_CATEGORIES,
  getCategoriesForState,
  getCategoryById,
  STATE_CONTEXTS,
} from '@/config/fraudCategories';
import {
  useStateHospitals,
  useCategoryPricing,
  computeOutliers,
  type HospitalOutlier,
} from '@/hooks/useFraudCategoryPricing';
import { CategoryOverviewPanel } from '@/components/investigator/CategoryOverviewPanel';
import { CategoryCodeTable } from '@/components/investigator/CategoryCodeTable';
import { HospitalOutlierTable } from '@/components/investigator/HospitalOutlierTable';
import { HospitalInvestigationModal } from '@/components/investigator/HospitalInvestigationModal';

// Patriotic palette
const NAVY = '#002868';
const RED = '#B22234';
const GOLD = '#C5A55A';
const CREAM = '#FAF9F6';

const PriceInvestigator = () => {
  const { state: stateParam } = useParams<{ state?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const brand = useBrand();
  const isAhp = brand.key === 'ahp';

  const state = stateParam?.toUpperCase() || 'MN';
  const categoryId = searchParams.get('category') || null;

  const [activeTab, setActiveTab] = useState<'codes' | 'hospitals'>('codes');
  const [investigatingOutlier, setInvestigatingOutlier] = useState<HospitalOutlier | null>(null);

  // State context banner data
  const stateCtx = STATE_CONTEXTS[state];
  const categories = getCategoriesForState(state);
  const selectedCategory = categoryId ? getCategoryById(categoryId) : null;

  // Phase 1: Fetch all hospitals in state
  const { data: hospitals = [], isLoading: hospitalsLoading } = useStateHospitals(state);

  const hospitalIds = useMemo(
    () => hospitals.map((h) => h.id),
    [hospitals]
  );

  // Phase 2: Fetch category pricing (lazy — only when category selected)
  const {
    data: categoryData,
    isLoading: categoryLoading,
  } = useCategoryPricing(selectedCategory ?? null, hospitalIds);

  // Compute outliers
  const outliers = useMemo(() => {
    if (!categoryData?.hospitalPricingMap) return [];
    return computeOutliers(categoryData.hospitalPricingMap, hospitals);
  }, [categoryData, hospitals]);

  // Hospital count by category
  const hospitalCountByCategory = useMemo(() => {
    const counts: Record<string, number> = {};
    if (categoryData?.hospitalPricingMap && categoryId) {
      counts[categoryId] = categoryData.hospitalPricingMap.size;
    }
    return counts;
  }, [categoryData, categoryId]);

  const handleCategorySelect = (id: string) => {
    setSearchParams({ category: id });
    setActiveTab('codes');
  };

  const pageTitle = isAhp ? 'Price Investigator' : 'Fraud Watchdog';
  const pageDescription = isAhp
    ? 'Investigate hospital pricing patterns across known fraud-risk categories'
    : 'Detect billing anomalies across Medicaid fraud-risk categories';

  return (
    <>
      <Helmet>
        <title>{pageTitle} — {state} | {brand.name}</title>
        <meta name="description" content={`${pageTitle}: ${pageDescription}. Analyze hospital pricing for ${state} Medicaid programs.`} />
      </Helmet>

      <div
        className="min-h-screen pb-24"
        style={{ backgroundColor: isAhp ? CREAM : '#000' }}
      >
        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-3">
              {isAhp ? (
                <div
                  className="p-2.5 rounded-xl"
                  style={{ backgroundColor: `${NAVY}10` }}
                >
                  <Shield className="w-7 h-7" style={{ color: NAVY }} />
                </div>
              ) : (
                <AlertTriangle className="w-8 h-8 text-[#00ff41]" />
              )}
              <div>
                <h1
                  style={{
                    fontFamily: isAhp ? "'Instrument Serif', Georgia, serif" : undefined,
                    color: isAhp ? '#1B1B1B' : '#00ff41',
                  }}
                  className={`text-3xl font-bold ${!isAhp ? 'font-mono' : ''}`}
                >
                  {pageTitle}
                </h1>
                <p style={{ color: isAhp ? 'rgba(27,27,27,0.62)' : '#888' }} className="text-sm mt-0.5">
                  {pageDescription}
                </p>
              </div>
            </div>
            {isAhp && (
              <div className="flex items-center gap-3 mt-3">
                <span
                  className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1 rounded-full"
                  style={{
                    backgroundColor: `${NAVY}12`,
                    color: NAVY,
                    border: `1px solid ${NAVY}25`,
                  }}
                >
                  <Star className="w-3.5 h-3.5" style={{ color: GOLD, fill: GOLD }} />
                  {state}
                </span>
                <span style={{ color: 'rgba(27,27,27,0.62)' }} className="text-sm">
                  {hospitals.length} hospitals
                </span>
              </div>
            )}
          </div>

          {/* State context banner — patriotic navy banner for AHP */}
          {stateCtx && isAhp && (
            <div
              className="mb-8 p-5 rounded-xl relative overflow-hidden"
              style={{
                background: `linear-gradient(135deg, ${NAVY} 0%, #001a4a 100%)`,
                boxShadow: '0 4px 20px rgba(0,40,104,0.25)',
              }}
            >
              {/* Decorative stars */}
              <div className="absolute top-2 right-4 flex gap-1.5 opacity-30">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3 h-3" style={{ color: GOLD, fill: GOLD }} />
                ))}
              </div>
              <p className="text-sm font-medium" style={{ color: CREAM }}>
                {stateCtx.description}
              </p>
              <div className="flex flex-wrap gap-4 mt-2.5">
                <span className="text-xs font-medium" style={{ color: `${GOLD}` }}>
                  {stateCtx.totalBilled} billed since {stateCtx.since}
                </span>
                <span className="text-xs font-bold" style={{ color: RED }}>
                  ~{stateCtx.estimatedFraud} estimated fraudulent
                </span>
                <span className="text-xs" style={{ color: 'rgba(250,249,246,0.6)' }}>
                  {stateCtx.programCount} programs under investigation
                </span>
              </div>
              {/* Gold accent line at bottom */}
              <div
                className="absolute bottom-0 left-0 right-0 h-0.5"
                style={{ background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }}
              />
            </div>
          )}
          {stateCtx && !isAhp && (
            <div className="mb-8 p-4 rounded-xl border bg-[#1a1a2e] border-gray-700">
              <p className="text-sm font-medium text-gray-200">{stateCtx.description}</p>
              <div className="flex flex-wrap gap-4 mt-2">
                <span className="text-xs text-gray-400">{stateCtx.totalBilled} billed since {stateCtx.since}</span>
                <span className="text-xs font-semibold text-red-400">~{stateCtx.estimatedFraud} estimated fraudulent</span>
                <span className="text-xs text-gray-400">{stateCtx.programCount} programs under investigation</span>
              </div>
            </div>
          )}

          {/* Loading state */}
          {hospitalsLoading && (
            <div className="flex items-center justify-center py-16">
              <Loader2
                className="w-8 h-8 animate-spin"
                style={{ color: isAhp ? NAVY : '#00ff41' }}
              />
            </div>
          )}

          {/* Category cards */}
          {!hospitalsLoading && (
            <>
              <div className="mb-8">
                <h2
                  className="text-lg font-semibold mb-4"
                  style={{
                    color: isAhp ? '#1B1B1B' : '#f0f0f0',
                    fontFamily: isAhp ? "'Instrument Serif', Georgia, serif" : undefined,
                  }}
                >
                  {isAhp ? 'Investigation Categories' : 'Fraud-Risk Categories'}
                </h2>
                <CategoryOverviewPanel
                  categories={categories.length > 0 ? categories : FRAUD_CATEGORIES}
                  selectedId={categoryId}
                  onSelect={handleCategorySelect}
                  hospitalCountByCategory={hospitalCountByCategory}
                />
              </div>

              {/* Category detail section */}
              {selectedCategory && (
                <div
                  className="mt-8 rounded-xl overflow-hidden"
                  style={isAhp ? {
                    backgroundColor: 'rgba(255,255,255,0.95)',
                    border: `1px solid rgba(0,0,0,0.1)`,
                    boxShadow: '0 2px 12px rgba(0,40,104,0.08)',
                  } : {
                    backgroundColor: '#1a1a2e',
                    border: '1px solid rgba(255,255,255,0.1)',
                  }}
                >
                  {/* Category detail header */}
                  <div
                    className="p-5"
                    style={isAhp ? {
                      background: `linear-gradient(135deg, rgba(0,40,104,0.06) 0%, rgba(0,40,104,0.02) 100%)`,
                      borderBottom: '1px solid rgba(0,0,0,0.08)',
                    } : {
                      borderBottom: '1px solid rgba(255,255,255,0.1)',
                    }}
                  >
                    <h3
                      className="text-lg font-semibold"
                      style={{
                        color: isAhp ? '#1B1B1B' : '#f0f0f0',
                        fontFamily: isAhp ? "'Instrument Serif', Georgia, serif" : undefined,
                      }}
                    >
                      {selectedCategory.name}
                    </h3>
                    <p
                      className="text-sm mt-1"
                      style={{ color: isAhp ? 'rgba(27,27,27,0.62)' : 'rgba(255,255,255,0.5)' }}
                    >
                      {selectedCategory.description}
                    </p>
                  </div>

                  {/* Tabs */}
                  <div
                    className="flex"
                    style={{
                      borderBottom: isAhp ? '1px solid rgba(0,0,0,0.08)' : '1px solid rgba(255,255,255,0.1)',
                    }}
                  >
                    {(['codes', 'hospitals'] as const).map((tab) => {
                      const isActive = activeTab === tab;
                      return (
                        <button
                          key={tab}
                          onClick={() => setActiveTab(tab)}
                          className="px-6 py-3 text-sm font-medium transition-colors border-b-2"
                          style={{
                            borderBottomColor: isActive
                              ? (isAhp ? GOLD : '#00ff41')
                              : 'transparent',
                            color: isActive
                              ? (isAhp ? NAVY : '#00ff41')
                              : (isAhp ? 'rgba(27,27,27,0.4)' : 'rgba(255,255,255,0.4)'),
                          }}
                        >
                          {tab === 'codes' ? 'Billing Codes' : `Hospital Outliers (${outliers.length})`}
                        </button>
                      );
                    })}
                  </div>

                  {/* Tab content */}
                  <div className="p-4">
                    {categoryLoading ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2
                          className="w-8 h-8 animate-spin"
                          style={{ color: isAhp ? NAVY : '#00ff41' }}
                        />
                      </div>
                    ) : activeTab === 'codes' ? (
                      <CategoryCodeTable
                        category={selectedCategory}
                        benchmarks={categoryData?.benchmarks || []}
                        hospitalPricingMap={categoryData?.hospitalPricingMap || new Map()}
                        totalHospitals={hospitals.length}
                      />
                    ) : (
                      <HospitalOutlierTable
                        outliers={outliers}
                        onInvestigate={setInvestigatingOutlier}
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Prompt to select a category */}
              {!selectedCategory && !hospitalsLoading && (
                <div className="text-center py-12">
                  <p
                    className="text-lg"
                    style={{
                      color: isAhp ? 'rgba(27,27,27,0.35)' : 'rgba(255,255,255,0.3)',
                      fontFamily: isAhp ? "'Instrument Serif', Georgia, serif" : undefined,
                    }}
                  >
                    Select a category above to investigate pricing patterns
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Investigation Modal */}
      <HospitalInvestigationModal
        isOpen={!!investigatingOutlier}
        onClose={() => setInvestigatingOutlier(null)}
        outlier={investigatingOutlier}
        category={selectedCategory}
      />
    </>
  );
};

export default PriceInvestigator;
